import * as Styles from './NewUserPage.styles';

import { ActionButton, CancelButton } from '@Components/Buttons';
import { Checkbox, FormControlLabel } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { User, defaultUserById } from '@Types/user.types';
import { clearUserById, createUser, createUserReset } from '@Actions/users';
import {
	defaultUserErrors,
	useCreateUserContext,
} from '../../contexts/CreateUserContext';
import {
	emailSchema,
	entitlementsSchema,
	nameSchema,
	newUserFormSchemaWithEntitlements,
	phoneNumberSchema,
	roleSchema,
} from '../../schemas/user.schema';
import { useDispatch, useSelector } from 'react-redux';

import { FormikProps } from 'formik';
import { NewUserForm } from '@Components/NewUserForm/NewUserForm';
import { addRouteNamespace } from '@Utils/actions';
import { getValue } from '@Selectors';
import {
	RegisteredApplications,
	selectApplicationName,
} from '@innovyze/stylovyze';
import { useHistory } from 'react-router-dom';
import { useNavigateTo } from '@Hooks';
import useValidateUserFromSchema from '../../hooks/useValidateUserFromSchema';
import { useGlobalization } from '@Translations/useGlobalization';
import { MAIN_ROUTES } from '@Types/routes';

const NewUserPage = (): JSX.Element => {
	const { t } = useGlobalization();
	useNavigateTo();
	const [anotherUserChecked, setAnotherUserChecked] = useState(false);
	const [formikProps, setNewUserFormikProps] = useState<
		Partial<FormikProps<User>>
	>({});

	const dispatch = useDispatch();
	const history = useHistory();
	const { created } = useSelector(getValue).users;
	const { setFieldValue, setFieldTouched, values } = formikProps;
	const validateFromSchema = useValidateUserFromSchema();
	const { setUserErrors } = useCreateUserContext();
	const applicationName = useSelector(selectApplicationName);

	const resetTextValueAndTouched = (name: string) => {
		if (setFieldValue && setFieldTouched) {
			setFieldValue(name, '');
			setFieldTouched(name, false);
		}
	};

	useEffect(() => {
		if (created && anotherUserChecked) {
			resetTextValueAndTouched('name');
			resetTextValueAndTouched('email');
			resetTextValueAndTouched('phoneNumber');
			dispatch(createUserReset());
		}
	}, [created, anotherUserChecked]);

	useEffect(() => {
		if (formikProps.setValues) {
			formikProps?.setValues(defaultUserById.user);
		}
		dispatch(clearUserById());
	}, [formikProps.setValues]);

	const handleSaveClick = () => {
		newUserFormSchemaWithEntitlements
			.validate(values)
			.then(() => {
				const redirectTo = !anotherUserChecked
					? MAIN_ROUTES.UserManagement
					: undefined;
				setUserErrors(defaultUserErrors);
				dispatch(
					createUser({
						user: values as User,
						redirectTo,
						applicationName:
							applicationName as RegisteredApplications,
					}),
				);
			})
			.catch(() => {
				validateFromSchema(nameSchema, values?.name, 'name');
				validateFromSchema(emailSchema, values?.email, 'email');
				validateFromSchema(roleSchema, values?.role, 'role');
				validateFromSchema(
					phoneNumberSchema,
					values?.phoneNumber,
					'phoneNumber',
				);
				validateFromSchema(
					entitlementsSchema,
					values?.entitlements,
					'entitlements',
				);
			});
	};

	return (
		<Styles.CustomPageWrapper
			title={t('New User')}
			applyPadding={true}
			isLoading={false}
			secondary={
				<>
					<FormControlLabel
						control={
							<Checkbox
								name="anotherUser"
								checked={anotherUserChecked}
								onChange={() => {
									setAnotherUserChecked(!anotherUserChecked);
								}}
								color="primary"
							/>
						}
						label={t('Add another user')}
					/>
					<CancelButton
						data-cy="cancelButton"
						style={{ marginLeft: 8 }}
						onClick={() => {
							history.push(addRouteNamespace('user-management'));
						}}>
						{t('Cancel')}
					</CancelButton>
					<ActionButton
						data-cy="saveButton"
						color="primary"
						style={{ marginLeft: 8 }}
						onClick={handleSaveClick}>
						{t('Save')}
					</ActionButton>
				</>
			}
			headerUnderline>
			<Styles.Container>
				<NewUserForm setNewUserFormikProps={setNewUserFormikProps} />
			</Styles.Container>
		</Styles.CustomPageWrapper>
	);
};

export default NewUserPage;
